// src/hooks/useTemplates.ts - VERIFICADO
import { useState, useEffect } from "react";
import type { DocumentTemplate } from "../types/domain";
import { templateRepository } from "../repositories/templateRepository";
import { logger } from "../lib/logging/logger";
import { useDebounceFn } from "./useDebounce";

interface UseTemplatesReturn {
  templates: DocumentTemplate[];
  loading: boolean;
  error: string | null;
  createTemplate: (templateData: {
    name: string;
    document_type: string;
    description: string;
  }) => Promise<{ success: boolean; error?: string }>;
  refreshTemplates: () => void;
}

export const useTemplates = (): UseTemplatesReturn => {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const result = await templateRepository.findAllTemplates();

      if (!result.success) {
        throw new Error(result.error || "Erro ao carregar templates");
      }

      setTemplates(result.data || []);
    } catch (err) {
      setError("Erro ao carregar templates: " + (err as Error).message);
      logger.error("loadTemplates.fail", { err });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const refreshTemplates = useDebounceFn(() => {
    logger.addBreadcrumb("templates.refresh");
    loadTemplates();
  }, 300);

  const createTemplate = async (templateData: {
    name: string;
    document_type: string;
    description: string;
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      setError(null);

      const result = await templateRepository.createTemplate(templateData);

      if (!result.success) {
        throw new Error(result.error || "Falha ao criar template");
      }

      // Atualiza a lista após criação bem-sucedida
      await loadTemplates();

      logger.addBreadcrumb("createTemplate.success", {
        name: templateData.name,
      });

      return { success: true };
    } catch (err) {
      const errorMessage = "Erro ao criar template: " + (err as Error).message;
      setError(errorMessage);
      logger.addBreadcrumb("createTemplate.error", {
        name: templateData.name,
        err,
      });
      return { success: false, error: errorMessage };
    }
  };

  return {
    templates,
    loading,
    error,
    createTemplate,
    refreshTemplates,
  };
};